import {announce} from '@github-ui/aria-live'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useEffect, useRef, useState, type ReactElement} from 'react'
import {NegativeFeedbackButton} from './Buttons'
import {FeedbackDetailsDialog} from './FeedbackDetailsDialog'
import type {FeedbackReasonSubmitter} from './hooks'
import styles from './NegativeFeedbackReasonControls.module.css'
import type {NegativeFeedbackReasonsConfig} from './types'

const SAVE_ERROR_MESSAGE = "Couldn't save your reason. Try again."

const ignoreClick = () => {}

interface NegativeFeedbackReasonControlsProps {
  commentId: string
  config: NegativeFeedbackReasonsConfig
  submitReason: FeedbackReasonSubmitter
}

export function NegativeFeedbackReasonControls({commentId, config, submitReason}: NegativeFeedbackReasonControlsProps) {
  const {
    options,
    menuLabel,
    otherValue = 'OTHER',
    otherDialogTitle = 'Tell us what was wrong',
    textResponseLabel,
  } = config
  const [selectedReason, setSelectedReason] = useState<string>()
  const [otherText, setOtherText] = useState<string>()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const [menuError, setMenuError] = useState<string>()
  // ActionMenu.Anchor overwrites refs on its child, so the shared ref goes through ActionMenu's anchorRef and
  // always points at whichever trigger (Add a reason or Change reason) is mounted.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inFlightRef = useRef(false)
  const pendingAnnouncementRef = useRef<{message: string; assertive?: boolean} | undefined>(undefined)

  // The vote just replaced 👎, so move focus to the next action instead of losing it.
  useEffect(() => {
    triggerRef.current?.focus()
  }, [])

  // After a save resolves, wait for the menu or dialog to finish closing, Change reason to replace Add a reason, and the
  // trigger to leave its loading state before moving focus. Then announce from the trigger so neither the focus
  // change nor the loading state change cuts the message off.
  useEffect(() => {
    const pending = pendingAnnouncementRef.current
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Change reason only exists after the commit, and focus must move after the menu or dialog has released it.
    if (!pending || menuOpen || dialogOpen || submitting) return
    pendingAnnouncementRef.current = undefined
    const trigger = triggerRef.current ?? undefined
    // Only move focus when the save left it nowhere (Add a reason was replaced by Change reason) or it's already on the
    // trigger, so a user who moved on during a slow save isn't pulled back.
    const active = document.activeElement
    if (!active || active === document.body || active === trigger) trigger?.focus()
    announce(pending.message, {assertive: pending.assertive, element: trigger})
  }, [selectedReason, menuOpen, dialogOpen, submitting])

  const commitReason = (value: string) => {
    pendingAnnouncementRef.current = {message: selectedReason === undefined ? 'Reason added' : 'Reason updated'}
    setSelectedReason(value)
  }

  // Resolves to undefined when another reason request is still in flight and this one was not sent. Callers reset
  // `submitting` after queuing their follow-up state, so both commit in the same render.
  const sendReason = async (value: string, textResponse?: string) => {
    if (inFlightRef.current) return undefined
    inFlightRef.current = true
    setSubmitting(true)
    try {
      return await submitReason(value, textResponse)
    } finally {
      inFlightRef.current = false
    }
  }

  const handlePick = async (value: string) => {
    setMenuError(undefined)
    if (value === otherValue) {
      setSubmitError(undefined)
      setDialogOpen(true)
      return
    }
    if (value === selectedReason) return

    const saved = await sendReason(value)
    if (saved === undefined) return
    if (saved) {
      commitReason(value)
    } else {
      setMenuError(SAVE_ERROR_MESSAGE)
      pendingAnnouncementRef.current = {message: SAVE_ERROR_MESSAGE, assertive: true}
    }
    setSubmitting(false)
  }

  const handleOtherSubmit = async (textResponse: string) => {
    setSubmitError(undefined)
    const saved = await sendReason(otherValue, textResponse)
    if (saved === undefined) return
    if (saved) {
      setOtherText(textResponse)
      setDialogOpen(false)
      commitReason(otherValue)
    } else {
      setSubmitError(SAVE_ERROR_MESSAGE)
    }
    setSubmitting(false)
  }

  const handleMenuOpenChange = (open: boolean) => {
    if (open && inFlightRef.current) return
    setMenuOpen(open)
  }

  const items = options.map(option => (
    <ActionList.Item
      key={option.value}
      selected={option.value === selectedReason}
      onSelect={() => void handlePick(option.value)}
    >
      {option.label}
    </ActionList.Item>
  ))

  const renderMenu = (trigger: ReactElement) => (
    <ActionMenu anchorRef={triggerRef} open={menuOpen} onOpenChange={handleMenuOpenChange}>
      <ActionMenu.Anchor>{trigger}</ActionMenu.Anchor>
      <ActionMenu.Overlay align="start">
        <ActionList selectionVariant="single">
          {menuLabel ? (
            <ActionList.Group>
              <ActionList.GroupHeading>{menuLabel}</ActionList.GroupHeading>
              {items}
            </ActionList.Group>
          ) : (
            items
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  const negativeButton = (
    <NegativeFeedbackButton
      disabled={false}
      ariaDisabled
      selected
      commentId={commentId}
      onClick={ignoreClick}
      showNegativeFeedbackModal={false}
    />
  )

  return (
    <>
      {selectedReason === undefined ? (
        <>
          {negativeButton}
          {renderMenu(
            <Button
              size="small"
              variant="invisible"
              className={styles.AddReason}
              trailingAction={TriangleDownIcon}
              loading={submitting}
            >
              Add a reason
            </Button>,
          )}
        </>
      ) : (
        <ButtonGroup>
          {negativeButton}
          {renderMenu(
            <Button
              size="small"
              variant="invisible"
              className={styles.ChangeReason}
              trailingAction={TriangleDownIcon}
              loading={submitting}
            >
              Change reason
            </Button>,
          )}
        </ButtonGroup>
      )}
      {menuError && (
        <InlineMessage variant="critical" size="small">
          {menuError}
        </InlineMessage>
      )}
      {dialogOpen && (
        <FeedbackDetailsDialog
          commentId={commentId}
          feedbackType="NEGATIVE"
          detailsOnly
          feedbackOptions={[]}
          title={otherDialogTitle}
          textResponseLabel={textResponseLabel}
          initialTextResponse={otherText}
          textResponseRequired
          submitting={submitting}
          submitError={submitError}
          returnFocusRef={triggerRef}
          onClose={() => {
            // Keep the dialog open while saving so a failure can still be shown and retried.
            if (!inFlightRef.current) setDialogOpen(false)
          }}
          onSubmit={({textResponse}) => void handleOtherSubmit(textResponse ?? '')}
        />
      )}
    </>
  )
}
